import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import api from '../api/http';
import { API_ENDPOINTS } from '../api/endpoints';
import { isFreeEmailDomain } from '../utils/emailDomain';
import './OrganizationSettings.css';
import { useLanguage } from '../context/LanguageContext';

export default function OrganizationSettings({ history }) {
  const { text, language } = useLanguage();
  const roleLabels = { owner: text('所有者', 'Owner'), billing_admin: text('請求管理者', 'Billing administrator'), member: text('メンバー', 'Member'), viewer: text('閲覧者', 'Viewer') };
  const statusLabels = { active: text('有効', 'Active'), invited: text('招待中', 'Invited'), suspended: text('停止中', 'Suspended') };
  const roleDescriptions = {
    owner: text('組織とすべての権限を管理できます', 'Can manage the organization and all permissions'),
    billing_admin: text('一般メンバーと閲覧者を管理できます', 'Can manage members and viewers'),
    member: text('組織の共有クレジットを利用できます', 'Can use organization credits'),
    viewer: text('読み取り専用（管理操作はできません）', 'Read-only access; management actions are unavailable'),
  };
  const [currentUser, setCurrentUser] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [inviteAllowance, setInviteAllowance] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [updatingMemberId, setUpdatingMemberId] = useState(null);
  const [creditOverview, setCreditOverview] = useState(null);
  const [requestCredits, setRequestCredits] = useState('');
  const [requestReason, setRequestReason] = useState('');

  const load = async () => {
    setLoading(true);
    setMessage('');
    try {
      const [meResponse, invitationResponse, creditResponse] = await Promise.all([
        api.get(API_ENDPOINTS.ME),
        api.get(API_ENDPOINTS.PENDING_INVITATIONS),
        api.get(API_ENDPOINTS.ORGANIZATION_CREDIT_REQUESTS),
      ]);
      const user = meResponse.data?.data?.user || null;
      setCurrentUser(user);
      setInvitations(invitationResponse.data?.invitations || []);
      setCreditOverview(creditResponse.data || null);

      if (user?.platformRole === 'owner' || user?.platformRole === 'billing_admin') {
        const membershipResponse = await api.get(API_ENDPOINTS.ORGANIZATION_MEMBERSHIPS);
        setMembers(membershipResponse.data?.memberships || []);
      } else {
        setMembers([]);
      }
    } catch (error) {
      const detail = error.response?.data?.error || error.response?.data?.message;
      setMessage(detail === 'Organization membership context is required'
        ? text('組織データベースの移行後に、この画面を利用できます。', 'This page will be available after the organization database migration.')
        : detail || text('組織情報を取得できませんでした。', 'Could not load organization information.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const invite = async (event) => {
    event.preventDefault();
    setMessage('');
    const localPart = email.trim();
    if (!organizationDomain || !/^[^\s@]+$/.test(localPart)) {
      setMessage(text('組織ドメイン用の有効なメール名を入力してください。', 'Enter a valid email name for the organization domain.'));
      return;
    }
    try {
      await api.post(API_ENDPOINTS.ORGANIZATION_INVITATIONS, {
        email: `${localPart}@${organizationDomain}`,
        platformRole: role,
        creditAllowance: inviteAllowance === '' ? null : Number(inviteAllowance),
      });
      setEmail('');
      setInviteAllowance('');
      setMessage(text('招待を作成しました。', 'Invitation created.'));
      await load();
    } catch (error) {
      setMessage(error.response?.data?.error || text('招待を作成できませんでした。', 'Could not create the invitation.'));
    }
  };

  const respond = async (id, action) => {
    try {
      await api.post(`${API_ENDPOINTS.ORGANIZATION_INVITATIONS}/${id}/${action}`);
      await load();
    } catch (error) {
      setMessage(error.response?.data?.error || '招待を更新できませんでした。');
    }
  };

  const saveAllowance = async (member, value) => {
    setUpdatingMemberId(member.id);
    setMessage('');
    try {
      await api.patch(`${API_ENDPOINTS.ORGANIZATION_MEMBERSHIPS}/${member.id}/allowance`, {
        creditAllowance: value === '' ? null : Number(value),
      });
      setMessage(text('メンバーのクレジット上限を更新しました。', 'Member credit allowance updated.'));
      await load();
    } catch (error) { setMessage(error.response?.data?.error || 'クレジット上限を更新できませんでした。'); }
    finally { setUpdatingMemberId(null); }
  };

  const returnUnusedAllowance = async (member) => {
    setUpdatingMemberId(member.id);
    setMessage('');
    try {
      await api.post(`${API_ENDPOINTS.ORGANIZATION_MEMBERSHIPS}/${member.id}/allowance/return-unused`);
      setMessage(text('メンバーの未使用クレジット枠を回収しました。', 'Unused member credits returned.'));
      await load();
    } catch (error) {
      setMessage(error.response?.data?.error || '未使用クレジット枠を回収できませんでした。');
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const submitCreditRequest = async (event) => {
    event.preventDefault();
    try {
      await api.post(API_ENDPOINTS.ORGANIZATION_CREDIT_REQUESTS, { credits: Number(requestCredits), reason: requestReason });
      setRequestCredits(''); setRequestReason(''); setMessage(text('追加クレジットを申請しました。', 'Additional credits requested.')); await load();
    } catch (error) { setMessage(error.response?.data?.error || '追加クレジットを申請できませんでした。'); }
  };

  const reviewCreditRequest = async (request, decision) => {
    try {
      await api.patch(`${API_ENDPOINTS.ORGANIZATION_CREDIT_REQUESTS}/${request.id}/review`, {
        decision, approvedCredits: decision === 'approved' ? request.requestedCredits : undefined,
      });
      setMessage(decision === 'approved' ? text('申請を承認しました。', 'Request approved.') : text('申請を却下しました。', 'Request rejected.')); await load();
    } catch (error) { setMessage(error.response?.data?.error || '申請を更新できませんでした。'); }
  };

  const updateMember = async (member, type, value) => {
    setUpdatingMemberId(member.id);
    setMessage('');
    try {
      await api.patch(`${API_ENDPOINTS.ORGANIZATION_MEMBERSHIPS}/${member.id}/${type}`, {
        [type === 'role' ? 'platformRole' : 'status']: value,
      });
      setMessage(text('メンバー情報を更新しました。', 'Member information updated.'));
      await load();
    } catch (error) {
      setMessage(error.response?.data?.error || 'メンバー情報を更新できませんでした。');
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const canManageMembers = currentUser?.platformRole === 'owner' || currentUser?.platformRole === 'billing_admin';
  const organizationDomain = (members.find((member) => member.platformRole === 'owner' && member.status === 'active')?.user?.email || '').split('@')[1] || '';
  const isFreeMailOrganization = organizationDomain !== '' && isFreeEmailDomain(organizationDomain);
  const canInviteFromOrganizationDomain = organizationDomain !== '' && !isFreeMailOrganization;
  const inviteRoles = currentUser?.platformRole === 'owner'
    ? Object.entries(roleLabels)
    : Object.entries(roleLabels).filter(([value]) => value === 'member' || value === 'viewer');
  const activeOwnerCount = members.filter((member) => member.platformRole === 'owner' && member.status === 'active').length;
  const isProtectedOwner = (member) => member.platformRole === 'owner' && member.status === 'active' && activeOwnerCount === 1;
  const canControlMember = (member) => !isProtectedOwner(member) && (currentUser?.platformRole === 'owner'
    || (currentUser?.platformRole === 'billing_admin' && ['member', 'viewer'].includes(member.platformRole)));

  useEffect(() => {
    if (!loading && isFreeMailOrganization) {
      history.replace('/');
    }
  }, [history, isFreeMailOrganization, loading]);

  if (!loading && isFreeMailOrganization) return null;

  return <main className="section organization-settings-page" style={{ paddingTop: 120, paddingBottom: 80, background: '#f7f9fc', minHeight: '100vh' }}>
    <div className="container organization-settings-container" style={{ maxWidth: 1280 }}>
      <Link to="/ocr-extract-view" className="organization-back-button">
        <FiArrowLeft aria-hidden="true" />
        {text('抽出画面へ戻る', 'Back to extraction')}
      </Link>
      <div style={{ marginBottom: 28 }}>
      <div style={eyebrow}>ORGANIZATION</div>
      <h1 style={{ margin: '6px 0 8px' }}>{text('組織・メンバー管理', 'Organization and member management')}</h1>
      <p>{canManageMembers
        ? text('組織のライセンス利用者と招待を安全に管理します。', 'Securely manage organization users and invitations.')
        : text('現在選択している組織と、自分への招待を確認します。', 'Review the selected organization and your invitations.')}</p></div>
      {message && <div role="status" style={notice}><span style={{ fontSize: 18 }}>✓</span>{message}</div>}
      {loading ? <p>{text('読み込み中...', 'Loading...')}</p> : <>
        {currentUser?.organizationId && <section style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <div><div style={sectionLabel}>{text('現在の組織', 'Current organization')}</div><h2 style={{ margin: '5px 0 8px' }}>{currentUser.companyName || text('現在の組織', 'Current organization')}</h2>
          <p style={{ margin: 0, color: '#64748b' }}>{roleDescriptions[currentUser.platformRole]}</p></div>
          <span style={roleBadge}>{roleLabels[currentUser.platformRole] || currentUser.platformRole}</span>
        </section>}

        {creditOverview?.ownMembership && <section style={{ ...card, marginTop: 20 }}>
          <div style={sectionLabel}>MY CREDIT ALLOWANCE</div><h2 style={sectionTitle}>{text('自分の利用枠', 'My credit allowance')}</h2>
          {creditOverview.ownMembership.creditAllowance == null
            ? <p>{text('組織の共有クレジットを上限なしで利用できます。', 'You can use organization credits without an individual limit.')}</p>
            : <><div style={{ display: 'flex', gap: 28, marginTop: 16 }}><strong>{text('上限', 'Limit')}: {creditOverview.ownMembership.creditAllowance}</strong><strong>{text('使用済み', 'Used')}: {creditOverview.ownMembership.creditsConsumed}</strong><strong>{text('残り', 'Remaining')}: {Math.max(0, creditOverview.ownMembership.creditAllowance - creditOverview.ownMembership.creditsConsumed)}</strong></div>
              <form onSubmit={submitCreditRequest} style={{ display: 'grid', gridTemplateColumns: '160px 1fr auto', gap: 12, marginTop: 20 }}>
                <input className="organization-control" type="number" min="1" required placeholder={text('希望数', 'Requested credits')} value={requestCredits} onChange={(e) => setRequestCredits(e.target.value)} style={input} />
                <input className="organization-control" maxLength="500" placeholder={text('申請理由（任意）', 'Reason (optional)')} value={requestReason} onChange={(e) => setRequestReason(e.target.value)} style={input} />
                <button className="button button-primary" type="submit">{text('追加を申請', 'Request credits')}</button>
              </form></>}
        </section>}

        {!canManageMembers && <section style={{ ...card, marginTop: 20, borderLeft: '4px solid #64748b' }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>管理権限について</h2>
          <p style={{ margin: 0, color: '#64748b' }}>{currentUser?.platformRole === 'viewer'
            ? '閲覧者は読み取り専用です。メンバーの追加、権限変更、停止はできません。'
            : 'メンバーの追加や権限変更は、所有者または請求管理者に依頼してください。'}</p>
        </section>}

        {canManageMembers && <section className="organization-members-card" style={{ ...card, marginTop: 20 }}>
          <div style={sectionHeader}><div><div style={sectionLabel}>MEMBERS</div><h2 style={sectionTitle}>{text('メンバー', 'Members')}</h2></div><span style={countBadge}>{language === 'en' ? members.length : `${members.length}名`}</span></div>
          <div className="organization-members-table-wrap"><table className="organization-members-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{[text('氏名', 'Name'), text('メール', 'Email'), text('役割', 'Role'), text('利用枠', 'Allowance'), text('状態', 'Status'), text('操作', 'Actions')].map((label) => <th key={label} style={tableHeader}>{label}</th>)}</tr></thead>
            <tbody>{members.map((member) => <tr key={member.id}>
              <td data-label="氏名" style={cell}>{member.user?.personName || member.user?.username}</td>
              <td data-label="メール" style={cell}>{member.user?.email}</td>
              <td data-label="役割" style={cell}>{canControlMember(member) ? <select className="organization-control organization-control--compact" aria-label={`${member.user?.email} の役割`} value={member.platformRole} disabled={updatingMemberId === member.id} onChange={(event) => updateMember(member, 'role', event.target.value)} style={compactSelect}>
                {(currentUser.platformRole === 'owner' ? Object.entries(roleLabels) : Object.entries(roleLabels).filter(([value]) => ['member', 'viewer'].includes(value))).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select> : <span style={lockedRole}>{roleLabels[member.platformRole] || member.platformRole}</span>}</td>
              <td data-label="利用枠" style={cell}>{member.platformRole === 'owner'
                ? <span style={sharedPoolBadge}>組織共有プール</span>
                : currentUser.platformRole === 'owner'
                  ? <AllowanceEditor member={member} disabled={updatingMemberId === member.id} onSave={saveAllowance} onReturn={returnUnusedAllowance} text={text} />
                  : member.creditAllowance == null ? text('共有プール', 'Shared pool') : `${member.creditsConsumed} / ${member.creditAllowance}`}</td>
              <td data-label="状態" style={cell}>{statusLabels[member.status] || member.status}</td>
              <td data-label="操作" style={cell}>{canControlMember(member) ? <button type="button" style={manageButton} disabled={updatingMemberId === member.id} onClick={() => updateMember(member, 'status', member.status === 'active' ? 'suspended' : 'active')}>{updatingMemberId === member.id ? text('更新中...', 'Updating...') : member.status === 'active' ? text('利用停止', 'Suspend') : text('再有効化', 'Reactivate')}</button> : <span style={{ color: '#64748b', fontSize: 12 }}>{isProtectedOwner(member) ? text('唯一の所有者（変更不可）', 'Sole owner (cannot be changed)') : text('所有者のみ操作可能', 'Owner action only')}</span>}</td>
            </tr>)}</tbody>
          </table></div>
          <p className="organization-credit-allocation-note">
            {text('分配したクレジットは所有者のクレジットから減算されません。メンバーがOCRを実行すると所有者のクレジットが消費されます。', 'Allocated credits are not deducted from the owner immediately. The owner’s credits are consumed when a member runs OCR.')}
          </p>
        </section>}

        {canManageMembers && canInviteFromOrganizationDomain && <section style={{ ...card, marginTop: 20 }}>
          <div style={sectionLabel}>INVITE USER</div><h2 style={sectionTitle}>{text('ユーザーを招待', 'Invite user')}</h2>
          <p style={{ color: '#64748b' }}>{text('登録済みユーザーのメールアドレスと付与する権限を指定してください。', 'Enter a registered user and choose their role.')}</p>
          <form onSubmit={invite} style={{ marginTop: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: currentUser.platformRole === 'owner' ? 'minmax(240px, 1fr) minmax(220px, .7fr) minmax(180px, .45fr)' : 'minmax(240px, 1fr) minmax(220px, .7fr)', gap: 16 }}>
            <label className="organization-field-label" style={fieldLabel}>{text('メールアドレス', 'Email address')}<div className="organization-email-field"><input className="organization-control organization-email-field__input" aria-label={text('招待するユーザーのメール名', 'Invitee email name')} type="text" value={email} onChange={(event) => setEmail(event.target.value.replace(/@.*$/, ''))} required placeholder="share" autoComplete="off" style={input} /><span className="organization-email-field__domain">@{organizationDomain}</span></div></label>
            <label className="organization-field-label" style={fieldLabel}>{text('役割', 'Role')}<select className="organization-control" aria-label={text('招待するユーザーの役割', 'Invitee role')} value={role} onChange={(event) => setRole(event.target.value)} style={input}>
              {inviteRoles.map(([value, label]) => <option key={value} value={value}>{label} — {roleDescriptions[value]}</option>)}
            </select></label>
            {currentUser.platformRole === 'owner' && <label className="organization-field-label" style={fieldLabel}>{text('クレジット利用上限（任意）', 'Credit allowance (optional)')}<input className="organization-control" aria-label={text('招待するユーザーのクレジット利用上限', 'Invitee credit allowance')} type="number" min="1" step="1" value={inviteAllowance} onChange={(event) => setInviteAllowance(event.target.value)} placeholder={text('共有プール', 'Shared pool')} style={input} /></label>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginTop: 18, flexWrap: 'wrap' }}>
              <span style={{ color: '#64748b', fontSize: 13 }}>{currentUser.platformRole === 'billing_admin' ? text('請求管理者はメンバーまたは閲覧者を招待できます。', 'Billing administrators can invite members or viewers.') : text('招待先が承認すると組織に追加されます。', 'The user joins the organization after accepting.')}</span>
              <button className="button button-primary" type="submit">{text('招待を送信', 'Send invitation')}</button>
            </div>
          </form>
        </section>}

        {currentUser?.platformRole === 'owner' && creditOverview?.requests?.some((item) => item.status === 'pending') && <section style={{ ...card, marginTop: 20 }}>
          <div style={sectionLabel}>CREDIT REQUESTS</div><h2 style={sectionTitle}>{text('追加クレジット申請', 'Additional credit requests')}</h2>
          {creditOverview.requests.filter((item) => item.status === 'pending').map((item) => <div key={item.id} style={invitationCard}>
            <div><strong>{item.membership?.user?.personName || item.membership?.user?.email}</strong><div style={{ color: '#64748b' }}>{item.requestedCredits} {text('クレジット', 'credits')} · {item.reason || text('理由なし', 'No reason provided')}</div></div>
            <div style={{ display: 'flex', gap: 8 }}><button className="button button-sm" onClick={() => reviewCreditRequest(item, 'rejected')}>{text('却下', 'Reject')}</button><button className="button button-primary button-sm" onClick={() => reviewCreditRequest(item, 'approved')}>{text('承認', 'Approve')}</button></div>
          </div>)}
        </section>}

        <section style={{ ...card, marginTop: 20 }}>
          <div style={sectionHeader}><div><div style={sectionLabel}>INVITATIONS</div><h2 style={sectionTitle}>{text('承認待ちの招待', 'Pending invitations')}</h2></div>{invitations.length > 0 && <span style={notificationBadge}>{invitations.length}</span>}</div>
          {invitations.length === 0 ? <div style={emptyState}>{text('現在、承認待ちの招待はありません。', 'There are no pending invitations.')}</div> : invitations.map((invitation) => <div key={invitation.id} style={invitationCard}>
            <div><strong style={{ display: 'block' }}>{invitation.organization?.name}</strong><span style={{ color: '#64748b', fontSize: 13 }}>{roleLabels[invitation.platformRole] || invitation.platformRole}として招待されています</span></div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="button button-sm" onClick={() => respond(invitation.id, 'decline')}>{text('辞退', 'Decline')}</button>
              <button className="button button-primary button-sm" onClick={() => respond(invitation.id, 'accept')}>{text('承認する', 'Accept')}</button>
            </div>
          </div>)}
        </section>
      </>}
    </div>
  </main>;
}

function AllowanceEditor({ member, disabled, onSave, onReturn, text }) {
  const [value, setValue] = useState(member.creditAllowance == null ? '' : String(member.creditAllowance));
  const unusedCredits = member.creditAllowance == null
    ? 0
    : Math.max(0, member.creditAllowance - member.creditsConsumed);
  return <div className="organization-allowance-editor">
    <input className="organization-control organization-control--compact" aria-label={text(`${member.user?.email} のクレジット上限`, `${member.user?.email} credit allowance`)} type="number" min="1" step="1" placeholder={text('共有', 'Shared')} value={value} disabled={disabled} onChange={(e) => setValue(e.target.value)} style={{ ...input, width: 100, minHeight: 36, background: '#fff', color: '#0f172a' }} />
    <button type="button" disabled={disabled} onClick={() => onSave(member, value)} style={manageButton}>{text('保存', 'Save')}</button>
    <small className="organization-credit-usage">{text('残り', 'Remaining')} {unusedCredits} / {text('使用', 'Used')} {member.creditsConsumed}</small>
    {unusedCredits > 0 && <button className="organization-return-credit-button" type="button" disabled={disabled} onClick={() => onReturn(member)}>{text('未使用分を回収', 'Return unused')}</button>}
  </div>;
}

const card = { padding: 24, background: '#fff', border: '1px solid #e5eaf1', borderRadius: 14, boxShadow: '0 4px 18px rgba(15, 23, 42, 0.04)' };
const eyebrow = { color: '#4054c7', fontSize: 12, fontWeight: 800, letterSpacing: '0.14em' };
const sectionLabel = { color: '#64748b', fontSize: 11, fontWeight: 800, letterSpacing: '0.12em' };
const sectionHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 };
const sectionTitle = { margin: '4px 0 0', fontSize: 21 };
const roleBadge = { padding: '8px 14px', color: '#3546b8', background: '#eef1ff', border: '1px solid #dce2ff', borderRadius: 999, fontWeight: 700 };
const countBadge = { padding: '5px 10px', color: '#475569', background: '#f1f5f9', borderRadius: 999, fontSize: 13, fontWeight: 700 };
const notificationBadge = { minWidth: 28, height: 28, display: 'inline-flex', justifyContent: 'center', alignItems: 'center', color: '#fff', background: '#4054c7', borderRadius: 999, fontWeight: 800 };
const cell = { padding: 14, borderBottom: '1px solid #edf0f4', textAlign: 'left' };
const tableHeader = { padding: '10px 14px', color: '#64748b', background: '#f8fafc', borderBottom: '1px solid #e5eaf1', textAlign: 'left', fontSize: 12 };
const fieldLabel = { display: 'flex', flexDirection: 'column', gap: 8, color: '#344054', fontSize: 13, fontWeight: 700 };
const input = { width: '100%', minHeight: 48, padding: '0 14px', border: '1px solid #cbd5e1', borderRadius: 9, fontSize: 14, boxSizing: 'border-box' };
const invitationCard = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20, padding: 18, background: '#f8faff', border: '1px solid #dfe5ff', borderRadius: 10, marginTop: 12 };
const emptyState = { padding: 24, color: '#64748b', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10, textAlign: 'center' };
const notice = { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, padding: '14px 16px', color: '#067647', background: '#ecfdf3', border: '1px solid #abefc6', borderRadius: 10, fontWeight: 700 };
const compactSelect = { minHeight: 38, padding: '0 30px 0 10px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8 };
const manageButton = { minHeight: 36, padding: '0 12px', color: '#475569', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, cursor: 'pointer', fontWeight: 700 };
const lockedRole = { display: 'inline-flex', alignItems: 'center', minHeight: 36, padding: '0 12px', color: '#475569', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 999, fontSize: 13, fontWeight: 700 };
const sharedPoolBadge = { display: 'inline-flex', alignItems: 'center', minHeight: 34, padding: '0 11px', color: '#475569', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 999, fontSize: 12, fontWeight: 700 };
